<template>
  <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="550px" persistent>
    <v-card class="rounded-lg">
      <v-card-title class="text-h6 font-weight-bold  bg-deep-orange-darken-1">
        Запланировать учебное событие
      </v-card-title>

      <v-card-text class="px-4">
        <v-form ref="eventForm" v-model="isFormValid">

          <!-- Выбор типа события -->
          <v-select
            v-model="newEvent.eventType"
            :items="eventTypeOptions"
            item-title="title"
            item-value="value"
            label="Тип планируемого события"
            variant="outlined"
            density="comfortable"
            class="mb-2"
          ></v-select>

          <!-- Название -->
          <v-text-field
            v-model="newEvent.title"
            label="Тема / Название занятия"
            variant="outlined"
            density="comfortable"
            :rules="[v => !!v || 'Название обязательно к заполнению']"
            class="mb-2"
          ></v-text-field>

          <!-- Описание -->
          <v-textarea
            v-model="newEvent.description"
            label="Краткое описание / План лекции"
            variant="outlined"
            density="comfortable"
            rows="2"
            auto-grow
            class="mb-2"
          ></v-textarea>

          <!-- Выбор Команд / Групп -->
          <v-autocomplete
            v-model="newEvent.targetTeamIds"
            :items="availableTeams"
            item-title="personGroupName"
            item-value="id"
            label="Для каких команд / групп"
            variant="outlined"
            density="comfortable"
            multiple
            chips
            closable-chips
            :loading="isTeamsLoading"
            :rules="[v => (v && v.length > 0) || 'Выберите как минимум одну группу']"
            class="mb-2"
          ></v-autocomplete>

          <!-- Блок Даты и Времени -->
          <v-row class="mb-2" no-gutters>
            <v-col cols="12" sm="6" class="pr-sm-2 mb-2 mb-sm-0">
              <v-text-field
                v-model="newEvent.date"
                label="Дата события"
                type="date"
                variant="outlined"
                density="comfortable"
                :rules="[v => !!v || 'Дата обязательна']"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="6" sm="3" class="pr-1">
              <v-text-field
                v-model="newEvent.startTime"
                label="Начало"
                type="time"
                variant="outlined"
                density="comfortable"
                :rules="[v => !!v || 'Время']"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="6" sm="3" class="pl-1" v-if="newEvent.eventType !== 'TASK'">
              <v-text-field
                v-model="newEvent.endTime"
                label="Конец"
                type="time"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- ДИНАМИЧЕСКИЕ ПОЛЯ ДЛЯ ВЕБИНАРА -->
          <v-expand-transition>
            <div v-if="newEvent.eventType === 'WEBINAR'" class="bg-blue-lighten-5 pa-3 rounded-lg mb-3 border border-blue-lighten-3">
              <v-text-field
                v-model="newEvent.webinarLink"
                label="Ссылка на конференцию (Zoom/MTS)"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-link"
                :rules="[v => !!v || 'Введите ссылку на онлайн-комнату']"
                class="mb-2"
                hide-details="auto"
              ></v-text-field>
              <v-text-field
                v-model="newEvent.accessCode"
                label="Код доступа / Пароль"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-lock-outline"
                hide-details="auto"
              ></v-text-field>
            </div>
          </v-expand-transition>
        </v-form>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer></v-spacer>
        <v-btn color="grey-darken-1" variant="text" @click="closeDialog">Отмена</v-btn>
        <v-btn color="deep-orange-darken-1" variant="flat" class="px-4" :disabled="!isFormValid" :loading="isSaving" @click="saveEvent">
          Сохранить
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue' // <-- Добавьте импорты макросов
import { customAxios } from "@/service/customAxios";

// Определение входящих параметров (Props) и событий (Emits)
const props = defineProps({
  modelValue: { type: Boolean, required: true },
  defaultDate: { type: String, default: '' }
})
const emit = defineEmits(['update:modelValue', 'saved'])

// Состояния валидации и отправки данных
const isFormValid = ref(false)
const isSaving = ref(false)
const isTeamsLoading = ref(false)
const eventForm = ref(null)

// Справочники для формы
const availableTeams = ref([])
const eventTypeOptions = [
  { title: 'Занятие / Лекция', value: 'LESSON' },
  { title: 'Онлайн-вебинар (Zoom)', value: 'WEBINAR' },
  { title: 'Задание / Дедлайн', value: 'TASK' },
  { title: 'Самостоятельное обучение', value: 'SELF_STUDY' }
]

// Чистая структура новой записи
const defaultEvent = {
  title: '',
  description: '',
  eventType: 'LESSON',
  date: '',
  startTime: '',
  endTime: '',
  webinarLink: '',
  accessCode: '',
  targetTeamIds: [],

}

const newEvent = ref({ ...defaultEvent })

// 📥 Загрузка списка команд с бэкенда для v-autocomplete
const fetchAvailableTeams = async () => {
  isTeamsLoading.value = true
  try {
    const response = await customAxios.get('/user_group')
    availableTeams.value = response.data
  } catch (e) {
    console.error('Ошибка загрузки списка команд для расписания:', e)
  } finally {
    isTeamsLoading.value = false
  }
}

// Управление закрытием диалога
const closeDialog = () => {
  emit('update:modelValue', false)
  eventForm.value?.resetValidation()
}

const saveEvent = async () => {
   if (!isFormValid.value) return
   isSaving.value = true
   try {
     // 1. Создаем объект даты из того, что выбрано в форме
     const dateObj = new Date(newEvent.value.date)
     // Сдвигаем на 1 день вперед
     dateObj.setDate(dateObj.getDate() - 1)
     // Собираем обратно в строку YYYY-MM-DD через шведский локаль
     const shiftedDate = dateObj.toLocaleDateString('sv')

     // 2. Сборка LocalDateTime с измененной датой
     const startIso = `${shiftedDate}T${newEvent.value.startTime}:00`
     const endIso = newEvent.value.endTime ? `${shiftedDate}T${newEvent.value.endTime}:00` : null

     if (newEvent.value.eventType !== 'WEBINAR') {
       newEvent.value.webinarLink = ''
       newEvent.value.accessCode = ''
     }

     // Отправляем на бэкенд
     await customAxios.post('/v1/schedule?currentUserId=1', {
       title: newEvent.value.title,
       description: newEvent.value.description,
       eventType: newEvent.value.eventType,
       startTime: startIso,
       endTime: endIso,
       webinarLink: newEvent.value.webinarLink,
       accessCode: newEvent.value.accessCode,
       targetTeamIds: newEvent.value.targetTeamIds,
     })

     emit('saved')
     closeDialog()
   } catch (e) {
     console.error('Не удалось сохранить событие расписания:', e)
   } finally {
     isSaving.value = false
   }
 }


// Следим за открытием окна, чтобы инициализировать форму и подтянуть команды
watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    newEvent.value = {
      ...defaultEvent,
      date: props.defaultDate || new Date().toISOString().split('T')[0]
    }
    fetchAvailableTeams()
  }
})
</script>